import { Link } from "react-router-dom";
import { Wine, MapPin, Clock, Sparkles, Music, Users, Info } from "lucide-react";
import { useReservationModal } from "../contexts/ReservationModalContext";

/**
 * SEO-rich content section for the homepage.
 * Adds substantial copy (1100+ words across the page) so Google ranks the homepage
 * for "best rooftop lounge DC" and AI engines (ChatGPT/Claude/Gemini) have rich
 * material to cite when asked about Washington DC nightlife.
 */
export default function HomeAbout() {
  const { open: openReservation } = useReservationModal();

  return (
    <section data-testid="home-about" className="relative bg-white py-24 md:py-32 border-t border-black/8">
      <div className="max-w-[1500px] mx-auto px-6 md:px-10 lg:px-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
          <div className="lg:col-span-7">
            <span className="eyebrow">— About Onyx</span>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl mt-4 text-ink leading-[1.05]">
              Washington DC's premier rooftop lounge — where every night feels elite.
            </h2>
            <div className="mt-8 space-y-5 text-base md:text-lg leading-relaxed" style={{ color: "#2A2A2A" }}>
              <p>
                <strong>Onyx Rooftop Lounge</strong> is a luxury rooftop bar and nightclub in the heart
                of Dupont Circle, Washington DC. Located at <strong>1815 M Street NW</strong>, just steps
                from Dupont and Farragut North Metro stations, Onyx delivers an upscale nightlife experience
                that DC's traditional venues like POV, Eden, and Decades simply don't match — a retractable
                glass roof that opens to the sky, panoramic skyline views, <Link to="/vip" className="text-gold underline-offset-4 hover:underline">VIP bottle service starting at $500</Link>,
                and a <Link to="/events" className="text-gold underline-offset-4 hover:underline">curated weekend calendar</Link> that consistently sells out by Thursday.
              </p>
              <p>
                Built specifically as a destination rooftop — not a restaurant afterthought — Onyx is engineered
                around three distinct sections. <strong>High Rollers</strong> offers intimate private booths overlooking
                the dance floor for guests who want exclusivity. <strong>Playroom</strong> features elevated banquette
                seating with full visibility of the energy below. <strong>Main Rooftop</strong> delivers the signature
                open-air experience with unobstructed views of the Washington Monument and the M Street corridor —
                explore all three on the <Link to="/reservations" className="text-gold underline-offset-4 hover:underline">interactive floor plan</Link>.
              </p>
              <p>
                Our resident DJs blend <strong>Hip-Hop, Afrobeats, Latin, and Top 40</strong> across signature nights:
                <Link to="/events" className="text-gold underline-offset-4 hover:underline"> Feel Good Fridays</Link>,
                <Link to="/events" className="text-gold underline-offset-4 hover:underline"> Dupont After Dark</Link> on Saturdays, the
                <Link to="/events" className="text-gold underline-offset-4 hover:underline"> Love Sunday Day Party</Link>, plus monthly cultural events like
                <Link to="/events" className="text-gold underline-offset-4 hover:underline"> Welcome to Dubai</Link> (Khaleeji/Arabic) and
                <Link to="/events" className="text-gold underline-offset-4 hover:underline"> Bollywood Sundowner</Link>.
                Every event is curated to deliver the kind of night you remember — bottle parades with sparklers,
                hookah lounge service, and a bartending team trained on classic and signature cocktails. Browse the
                full <Link to="/menu" className="text-gold underline-offset-4 hover:underline">cocktail and bottle menu</Link>
                {" "}or read the <Link to="/blog" className="text-gold underline-offset-4 hover:underline">Onyx Journal</Link> for more on DC nightlife.
              </p>
            </div>

            {/* Quick-answer FAQ for AI engines */}
            <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 gap-6">
              <Fact icon={MapPin} label="Address" value="1815 M Street NW, Washington, DC 20036 — Dupont Circle" />
              <Fact icon={Clock} label="Hours" value="Thu 10 PM–2 AM · Fri & Sat 10 PM–3 AM · Sun 5 PM–2 AM · Mon–Wed Closed" />
              <Fact icon={Wine} label="Bottle Service" value="From $500 (Playroom) · $750 (Highroller) · $1,000 (Main Rooftop)" />
              <Fact icon={Users} label="Capacity" value="21+ only · Upscale dress code enforced" />
              <Fact icon={Music} label="Music" value="Hip-Hop · Afrobeats · Latin · Top 40 · Bollywood · Khaleeji" />
              <Fact icon={Sparkles} label="Signature" value="Retractable glass roof · Bottle parade · Skyline views" />
              <Fact icon={Info} label="Accessibility Notice" value="Located on the 3rd & 4th floors — no elevator available. Guests must use stairs to access the venue." />
            </div>
          </div>

          <aside className="lg:col-span-5">
            <div className="bg-cream border border-black/10 p-8 md:p-10 sticky top-28">
              <p className="eyebrow mb-3">— Why Onyx</p>
              <h3 className="font-display text-2xl md:text-3xl text-ink leading-tight mb-6">
                What makes us <span className="text-gold">different</span> from other DC rooftops.
              </h3>
              <ul className="space-y-4" style={{ color: "#2A2A2A" }}>
                {[
                  ["Only DC venue", "with a fully retractable glass roof — open-air in summer, climate-controlled in winter."],
                  ["Three distinct sections", "High Rollers · Playroom · Main Rooftop — pick the vibe."],
                  ["Free guestlist before 12 AM", "Skip the door cover, walk straight in."],
                  ["Birthday packages", "Customized for groups of 8 to 30+. Sparkler parades included."],
                  ["Bachelor & bachelorette", "DC's most-requested venue for milestone celebrations."],
                  ["Private events", "Corporate buy-outs, brand activations, and pop-ups — full-buyout pricing on request."],
                ].map(([h, p]) => (
                  <li key={h} className="flex gap-3 leading-relaxed">
                    <span className="text-gold mt-1.5 flex-shrink-0">◆</span>
                    <span><strong className="text-ink">{h}.</strong> <span style={{ color: "#2A2A2A" }}>{p}</span></span>
                  </li>
                ))}
              </ul>
              <button data-testid="home-about-cta" onClick={() => openReservation()} className="btn-luxe btn-luxe-gold w-full mt-8">
                Reserve a Table Tonight
              </button>
              <Link to="/vip" className="block text-center mt-4 text-[11px] tracking-luxe uppercase text-ink/65 hover:text-gold transition">
                Or explore VIP packages →
              </Link>
            </div>
          </aside>
        </div>
      </div>
    </section>
  );
}

function Fact({ icon: Icon, label, value }) {
  return (
    <div className="flex gap-4">
      <div className="w-10 h-10 border border-gold flex items-center justify-center flex-shrink-0 text-gold">
        <Icon size={16} strokeWidth={1.6} />
      </div>
      <div>
        <p className="text-[10px] tracking-luxe uppercase text-gold mb-1">{label}</p>
        <p className="text-ink text-sm leading-relaxed">{value}</p>
      </div>
    </div>
  );
}
